import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaArrowLeft, 
  FaUsers, 
  FaUserPlus,
  FaUserMinus,
  FaUserShield,
  FaCrown,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
  FaEnvelope,
  FaUserCog
} from 'react-icons/fa';
import { supabase } from '../../../data/supabaseClient';
import { AuthContext } from '../../../Provider';
import { useToast } from '../../../hooks/ToastContext';
import Spinner from '../../../common/Spinner';
import { CustomButton2, CustomButton8 } from '../../../Templates';
import '../../styles/GroupsPage.css';

const GroupMembersManagement = () => {
  const { id: groupId } = useParams();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [activeTab, setActiveTab] = useState('members');
  
  // Load group data
  useEffect(() => {
    const fetchGroupData = async () => {
      if (!groupId || !user) {
        navigate('/login');
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch group details
        const { data: groupData, error: groupError } = await supabase
          .from('groups')
          .select('*')
          .eq('id', groupId)
          .single();
        
        if (groupError) throw groupError;
        
        setGroup(groupData);
        
        // Check if user is an admin of the group
        const { data: memberData, error: memberError } = await supabase
          .from('group_members')
          .select('role')
          .eq('group_id', groupId)
          .eq('user_id', user.id)
          .eq('status', 'active')
          .single();
        
        if (memberError) {
          // If not a member/admin, redirect back to the group page
          addToast({
            type: 'error',
            title: t('errors.access_denied'),
            description: t('errors.admin_required')
          });
          navigate(`/groups/${groupId}`);
          return;
        }
        
        setUserRole(memberData.role);
        
        // Only allow admins and moderators to access this page
        if (memberData.role !== 'admin' && memberData.role !== 'moderator') {
          addToast({
            type: 'error',
            title: t('errors.access_denied'),
            description: t('errors.admin_required')
          });
          navigate(`/groups/${groupId}`);
          return;
        }
        
        // Fetch group members
        const { data: membersData, error: membersError } = await supabase
          .from('group_members')
          .select('*, profiles(*)')
          .eq('group_id', groupId)
          .eq('status', 'active')
          .order('role', { ascending: false });
        
        if (membersError) throw membersError;
        
        setMembers(membersData || []);
        
        // Fetch join requests (admin only)
        if (memberData.role === 'admin') {
          // Use a simpler query to avoid relationship issues
          const { data: requestsData, error: requestsError } = await supabase
            .from('group_join_requests')
            .select('id, group_id, user_id, status, created_at, responded_by, responded_at, message')
            .eq('group_id', groupId)
            .eq('status', 'pending');
          
          if (requestsError) throw requestsError;
          
          // If we have requests, fetch the user profiles separately
          if (requestsData && requestsData.length > 0) {
            const userIds = requestsData.map(req => req.user_id);
            
            const { data: profilesData, error: profilesError } = await supabase
              .from('profiles')
              .select('id, username, avatar_url')
              .in('id', userIds);
              
            if (!profilesError && profilesData) {
              // Merge the profile data with the requests data
              const mergedRequests = requestsData.map(req => {
                const profile = profilesData.find(p => p.id === req.user_id);
                return {
                  ...req,
                  profiles: profile
                };
              });
              
              setJoinRequests(mergedRequests || []);
            } else {
              setJoinRequests(requestsData.map(req => ({
                ...req,
                profiles: null
              })));
            }
          } else {
            setJoinRequests([]);
          }
        }
        
      } catch (error) {
        console.error('Error fetching group data:', error);
        addToast({
          type: 'error',
          title: t('errors.fetch_error'),
          description: t('errors.group_fetch_failed')
        });
        navigate('/groups');
      } finally {
        setLoading(false);
      }
    };
    
    fetchGroupData();
  }, [groupId, user, navigate, t, addToast]);
  
  // Promote a member to moderator
  const handlePromoteMember = async (memberId) => {
    // Only admins can promote members
    if (userRole !== 'admin') {
      addToast({
        type: 'error',
        title: t('errors.access_denied'),
        description: t('errors.admin_required')
      });
      return;
    }
    
    try {
      const { error } = await supabase
        .from('group_members')
        .update({ role: 'moderator' })
        .eq('id', memberId);
      
      if (error) throw error;
      
      // Update UI
      setMembers(prevMembers => prevMembers.map(member => 
        member.id === memberId ? { ...member, role: 'moderator' } : member
      ));
      
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.member_promoted')
      });
      
    } catch (error) {
      console.error('Error promoting member:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.promote_member_failed')
      });
    }
  };
  
  // Demote a moderator to regular member
  const handleDemoteMember = async (memberId) => {
    // Only admins can demote members
    if (userRole !== 'admin') {
      addToast({
        type: 'error',
        title: t('errors.access_denied'),
        description: t('errors.admin_required')
      });
      return;
    }
    
    try {
      const { error } = await supabase
        .from('group_members')
        .update({ role: 'member' })
        .eq('id', memberId);
      
      if (error) throw error;
      
      // Update UI
      setMembers(prevMembers => prevMembers.map(member => 
        member.id === memberId ? { ...member, role: 'member' } : member
      ));
      
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.member_demoted')
      });
      
    } catch (error) {
      console.error('Error demoting member:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.demote_member_failed')
      });
    }
  };
  
  // Remove a member from the group
  const handleRemoveMember = async (memberId, memberUserId) => {
    // Cannot remove yourself
    if (memberUserId === user.id) {
      addToast({
        type: 'error',
        title: t('errors.remove_error'),
        description: t('errors.cannot_remove_self')
      });
      return;
    }
    
    // As moderator, cannot remove other moderators or admins
    if (userRole === 'moderator') {
      const targetMember = members.find(m => m.id === memberId);
      if (targetMember && (targetMember.role === 'admin' || targetMember.role === 'moderator')) {
        addToast({
          type: 'error',
          title: t('errors.remove_error'),
          description: t('errors.cannot_remove_admin_or_moderator')
        });
        return;
      }
    }
    
    try {
      // Set member status to inactive (soft delete)
      const { error } = await supabase
        .from('group_members')
        .update({ status: 'inactive' })
        .eq('id', memberId);
      
      if (error) throw error;
      
      // Update UI
      setMembers(prevMembers => prevMembers.filter(member => member.id !== memberId));
      
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.member_removed')
      });
      
    } catch (error) {
      console.error('Error removing member:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.remove_member_failed')
      });
    }
  };
  
  // Handle join request approval
  const handleApproveRequest = async (requestId, userId) => {
    try {
      // Update request status to approved
      const { error: requestError } = await supabase
        .from('group_join_requests')
        .update({
          status: 'approved',
          responded_by: user.id,
          responded_at: new Date().toISOString()
        })
        .eq('id', requestId);
      
      if (requestError) throw requestError;
      
      // Add user as a group member
      const { error: memberError } = await supabase
        .from('group_members')
        .insert({
          group_id: groupId,
          user_id: userId,
          role: 'member',
          status: 'active'
        });
      
      if (memberError) throw memberError;
      
      // Update UI
      setJoinRequests(prevRequests => prevRequests.filter(request => request.id !== requestId));
      
      // Refresh members list
      const { data: membersData, error: membersError } = await supabase
        .from('group_members')
        .select('*, profiles(*)')
        .eq('group_id', groupId)
        .eq('status', 'active')
        .order('role', { ascending: false });
      
      if (!membersError) {
        setMembers(membersData || []);
      }
      
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.request_approved')
      });
      
    } catch (error) {
      console.error('Error approving request:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.approve_request_failed')
      });
    }
  };
  
  // Handle join request rejection
  const handleRejectRequest = async (requestId) => {
    try {
      // Update request status to rejected
      const { error } = await supabase
        .from('group_join_requests')
        .update({
          status: 'rejected',
          responded_by: user.id,
          responded_at: new Date().toISOString()
        })
        .eq('id', requestId);
      
      if (error) throw error;
      
      // Update UI
      setJoinRequests(prevRequests => prevRequests.filter(request => request.id !== requestId));
      
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.request_rejected')
      });
      
    } catch (error) {
      console.error('Error rejecting request:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.reject_request_failed')
      });
    }
  };
  
  // Navigate back to group
  const handleBack = () => {
    navigate(`/groups/${groupId}`);
  };
  
  if (loading) {
    return (
      <>
        <div className="groups-page">
          <Spinner size="large" />
        </div>
      </>
    );
  }
  
  if (!group) {
    return (
      <>
        <div className="groups-page">
          <div className="groups-empty">
            <h3>{t('groups.group_not_found')}</h3>
            <CustomButton2
              label={t('groups.back_to_groups')}
              onClick={() => navigate('/groups')}
            />
          </div>
        </div>
      </>
    );
  }
  
  return (
    <>
      <div className="groups-page">
        <div className="back-button" style={{ marginBottom: '20px' }}>
          <CustomButton8 onClick={handleBack}>
            <FaArrowLeft />
          </CustomButton8>
        </div>
        
        <h2 className="form-heading">{t('groups.manage_members')}</h2>
        <p className="group-context">{t('groups.for_group')}: <strong>{group.name}</strong></p>
        
        <div className="group-tabs">
          <div 
            className={`group-tab ${activeTab === 'members' ? 'active' : ''}`}
            onClick={() => setActiveTab('members')}
          >
            <FaUsers />
            <span>{t('groups.members')} ({members.length})</span>
          </div>
          
          {userRole === 'admin' && (
            <div 
              className={`group-tab ${activeTab === 'requests' ? 'active' : ''}`}
              onClick={() => setActiveTab('requests')}
            >
              <FaUserPlus />
              <span>{t('groups.join_requests')} ({joinRequests.length})</span>
            </div>
          )}
        </div>
        
        {activeTab === 'members' && (
          <div className="members-management">
            {members.length === 0 ? (
              <div className="empty-list-message">
                <p>{t('groups.no_members')}</p>
              </div>
            ) : (
              <div className="members-list-management">
                <table className="members-table">
                  <thead>
                    <tr>
                      <th>{t('groups.member')}</th>
                      <th>{t('groups.role')}</th>
                      <th>{t('groups.joined')}</th>
                      <th>{t('groups.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map(member => (
                      <tr key={member.id}>
                        <td className="member-info">
                          <div 
                            className="member-avatar"
                            style={{
                              backgroundImage: member.profiles?.avatar_url 
                                ? `url(${member.profiles.avatar_url})` 
                                : 'none'
                            }}
                          />
                          <span>{member.profiles?.username || t('groups.unknown_user')}</span>
                        </td>
                        <td className="member-role">
                          {member.role === 'admin' ? (
                            <span className="role admin">
                              <FaCrown />
                              {t('groups.roles.admin')}
                            </span>
                          ) : member.role === 'moderator' ? (
                            <span className="role moderator">
                              <FaUserShield />
                              {t('groups.roles.moderator')}
                            </span>
                          ) : (
                            <span className="role member">
                              <FaUsers />
                              {t('groups.roles.member')}
                            </span>
                          )}
                        </td>
                        <td>
                          {new Date(member.joined_at).toLocaleDateString()}
                        </td>
                        <td className="member-actions">
                          {userRole === 'admin' && member.user_id !== user.id && (
                            <>
                              {member.role === 'member' ? (
                                <CustomButton2
                                  label={t('groups.promote')}
                                  onClick={() => handlePromoteMember(member.id)}
                                  icon={<FaUserShield />}
                                  small
                                />
                              ) : member.role === 'moderator' ? (
                                <CustomButton2
                                  label={t('groups.demote')}
                                  onClick={() => handleDemoteMember(member.id)}
                                  icon={<FaUsers />}
                                  small
                                />
                              ) : null}
                              
                              <CustomButton2
                                label={t('groups.remove')}
                                onClick={() => handleRemoveMember(member.id, member.user_id)}
                                icon={<FaUserMinus />}
                                small
                                danger
                              />
                            </>
                          )}
                          
                          {userRole === 'moderator' && 
                           member.role === 'member' && 
                           member.user_id !== user.id && (
                            <CustomButton2
                              label={t('groups.remove')}
                              onClick={() => handleRemoveMember(member.id, member.user_id)}
                              icon={<FaUserMinus />}
                              small
                              danger
                            />
                          )}
                          
                          {(member.user_id === user.id || member.role === 'admin') && (
                            <div className="no-actions">
                              {member.user_id === user.id ? t('groups.you') : t('groups.no_actions')}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'requests' && userRole === 'admin' && (
          <div className="join-requests-management">
            {joinRequests.length === 0 ? (
              <div className="empty-list-message">
                <FaUserPlus className="empty-icon" />
                <p>{t('groups.no_pending_requests')}</p>
              </div>
            ) : (
              <div className="requests-list">
                <table className="requests-table">
                  <thead>
                    <tr>
                      <th>{t('groups.user')}</th>
                      <th>{t('groups.requested_on')}</th>
                      <th>{t('groups.message')}</th>
                      <th>{t('groups.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {joinRequests.map(request => (
                      <tr key={request.id}>
                        <td className="requester-info">
                          <div 
                            className="requester-avatar"
                            style={{
                              backgroundImage: request.profiles?.avatar_url 
                                ? `url(${request.profiles.avatar_url})` 
                                : 'none'
                            }}
                          />
                          <span>{request.profiles?.username || t('groups.unknown_user')}</span>
                        </td>
                        <td>
                          {new Date(request.created_at).toLocaleDateString()}
                        </td>
                        <td className="request-message">
                          {request.message || t('groups.no_message')}
                        </td>
                        <td className="request-actions">
                          <CustomButton2
                            label={t('groups.approve')}
                            onClick={() => handleApproveRequest(request.id, request.user_id)}
                            icon={<FaCheck />}
                            small
                          />
                          <CustomButton2
                            label={t('groups.reject')}
                            onClick={() => handleRejectRequest(request.id)}
                            icon={<FaTimes />}
                            small
                            danger
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default GroupMembersManagement;
